import { Tooltip } from "antd";
import {
  LoginOutlined, MobileOutlined, TabletOutlined, DesktopOutlined, GlobalOutlined,
} from "@ant-design/icons";
import { fetchLogins } from "../../../services/customer360Service";
import {
  cardStyle, dateTime, relative, chip, TONE,
  PanelHeader, PanelTable, PanelSkeleton, PanelError, usePanelData,
} from "./shared";

/**
 * Customer-side login sessions.
 *
 * These are the customer's own logins to the product, not CMS staff logins —
 * those live in a separate table and never appear here.
 */

/**
 * A user agent, read the way a person would describe it.
 *
 * Deliberately shallow: the full string stays in a tooltip, because the column
 * is scanned for "was this the same device" rather than parsed for a version.
 */
const parseAgent = (ua = "") => {
  const s = String(ua);
  if (!s) return { icon: <GlobalOutlined />, label: "Unknown" };

  const mobile = /iPhone|Android.*Mobile|Windows Phone/i.test(s);
  const tablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(s);

  const os = /iPhone|iPad|iPod/i.test(s) ? "iOS"
    : /Android/i.test(s) ? "Android"
      : /Windows/i.test(s) ? "Windows"
        : /Mac OS X|Macintosh/i.test(s) ? "macOS"
          : /Linux/i.test(s) ? "Linux" : "";

  /* Order matters — Edge and Chrome both claim to be Safari, and Chrome also
     claims to be Safari, so the most specific name has to win. */
  const browser = /Edg\//i.test(s) ? "Edge"
    : /OPR\/|Opera/i.test(s) ? "Opera"
      : /Chrome\//i.test(s) ? "Chrome"
        : /Firefox\//i.test(s) ? "Firefox"
          : /Safari\//i.test(s) ? "Safari" : "";

  return {
    icon: mobile ? <MobileOutlined /> : tablet ? <TabletOutlined /> : <DesktopOutlined />,
    label: [browser, os].filter(Boolean).join(" · ") || "Unknown",
  };
};

/**
 * What a session is doing now.
 *
 * Revoked outranks expired: a session someone ended deliberately is a different
 * fact from one that simply ran out, and the first is the one worth seeing.
 */
const sessionState = (row) => {
  if (row.revoked_at) return chip("Revoked", TONE.red);
  if (Number(row.is_active) !== 1) return chip("Expired", TONE.slate);
  if (row.expires_at && new Date(row.expires_at) < new Date()) return chip("Expired", TONE.slate);
  return chip("Active", TONE.green);
};

const LoginActivityTab = ({ userId, active }) => {
  const p = usePanelData(fetchLogins, userId, active);

  if (p.loading && !p.data) return <PanelSkeleton />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const columns = [
    {
      title: "User", dataIndex: "user_name", width: 170,
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>,
    },
    {
      title: "Device", key: "device", width: 190,
      render: (_, r) => {
        const { icon, label } = parseAgent(r.user_agent);
        return (
          <Tooltip title={r.user_agent || "No user agent recorded"}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#334155" }}>
              <span style={{ color: "#94a3b8" }}>{icon}</span>
              {label}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "IP", dataIndex: "ip", width: 140,
      render: (v) => <span style={{ fontSize: 12, color: "#475569", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
    },
    {
      title: "Signed in", dataIndex: "created_at", width: 180,
      render: (v) => (
        <Tooltip title={dateTime(v)}>
          <span style={{ fontSize: 12.5, color: "#334155", whiteSpace: "nowrap" }}>{relative(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Last used", dataIndex: "last_used_at", width: 180,
      render: (v) => (v
        ? <Tooltip title={dateTime(v)}><span style={{ fontSize: 12.5, color: "#64748b", whiteSpace: "nowrap" }}>{relative(v)}</span></Tooltip>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Expires", dataIndex: "expires_at", width: 175,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    { title: "State", key: "state", width: 110, render: (_, r) => sessionState(r) },
  ];

  return (
    <div style={cardStyle}>
      <PanelHeader
        icon={<LoginOutlined />}
        title="Login activity"
        subtitle="The customer's own sessions — newest first"
        iconBg="linear-gradient(135deg,#2563eb,#3b82f6)"
      />
      <PanelTable
        columns={columns}
        rows={p.rows}
        loading={p.loading}
        page={p.page}
        total={p.total}
        perPage={p.perPage}
        onPage={p.goToPage}
        empty="No login sessions recorded for this account yet."
        scrollX={1145}
      />
    </div>
  );
};

export default LoginActivityTab;
